import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * A small rich text field built on contentEditable — no editor package.
 *
 * The value is an HTML string restricted to the tag set below. Everything that
 * comes out of the field goes through sanitize() first, so what gets stored is
 * predictable enough to render the same way in three places: this page, the
 * Chromium-printed service ticket, and (as plain text) the technician app.
 *
 * Anything already stored as plain text keeps working: fromValue() escapes it
 * and turns its newlines into <br>, so old tickets and anything the technician
 * app writes still open correctly here.
 */

// The whole vocabulary of the field. Anything else is unwrapped to its text.
const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "BR", "P", "DIV"]);
const ALLOWED_ALIGN = new Set(["left", "center", "right", "justify"]);

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// A value is HTML only if it actually carries one of our tags; everything else
// is treated as the plain text it is.
export const looksLikeHtml = (value) => /<(\/?)(b|strong|i|em|u|ul|ol|li|br|p|div)\b[^>]*>/i.test(value || "");

export const toEditorHtml = (value) => {
  if (!value) return "";
  if (looksLikeHtml(value)) return sanitizeHtml(value);
  return escapeHtml(value).replace(/\r?\n/g, "<br>");
};

/** Strip everything outside the whitelist, keeping the text inside it. */
export function sanitizeHtml(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");

  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) return;
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        return;
      }
      walk(child);
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Keep the words, drop the wrapper.
        child.replaceWith(...child.childNodes);
        return;
      }
      const align = child.style?.textAlign;
      [...child.attributes].forEach((attribute) => child.removeAttribute(attribute.name));
      if (align && ALLOWED_ALIGN.has(align)) child.style.textAlign = align;
    });
  };
  walk(doc.body);

  return doc.body.innerHTML;
};

/** True when the field holds nothing but empty markup, so the placeholder shows. */
const isEmptyHtml = (html) =>
  !html || !html.replace(/<br\s*\/?>/gi, "").replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();

const COMMANDS = [
  { key: "bold", command: "bold", label: "Bold" },
  { key: "italic", command: "italic", label: "Italic" },
  { key: "underline", command: "underline", label: "Underline" },
  { key: "divider" },
  { key: "justifyLeft", command: "justifyLeft", label: "Align left" },
  { key: "justifyCenter", command: "justifyCenter", label: "Align center" },
  { key: "justifyRight", command: "justifyRight", label: "Align right" },
  { key: "divider2" },
  { key: "insertOrderedList", command: "insertOrderedList", label: "Numbered list" },
  { key: "insertUnorderedList", command: "insertUnorderedList", label: "Bulleted list" },
];

function Glyph({ name }) {
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "bold":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <path d="M6 4h7a4 4 0 0 1 0 8H6zM6 12h8a4 4 0 0 1 0 8H6z" />
        </svg>
      );
    case "italic":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="19" y1="4" x2="10" y2="4" />
          <line x1="14" y1="20" x2="5" y2="20" />
          <line x1="15" y1="4" x2="9" y2="20" />
        </svg>
      );
    case "underline":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <path d="M6 4v6a6 6 0 0 0 12 0V4" />
          <line x1="4" y1="20" x2="20" y2="20" />
        </svg>
      );
    case "justifyLeft":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="14" y2="12" />
          <line x1="3" y1="18" x2="18" y2="18" />
        </svg>
      );
    case "justifyCenter":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="6" y1="12" x2="18" y2="12" />
          <line x1="5" y1="18" x2="19" y2="18" />
        </svg>
      );
    case "justifyRight":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="6" y1="18" x2="21" y2="18" />
        </svg>
      );
    case "insertOrderedList":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <path d="M4 6h1v4M4 10h2" strokeWidth="1.6" />
          <path d="M3.5 16.5c0-.8 1.5-.8 1.5 0S3.5 17.5 3.5 18.5H5.5" strokeWidth="1.6" />
        </svg>
      );
    case "insertUnorderedList":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
          <line x1="9" y1="6" x2="21" y2="6" />
          <line x1="9" y1="12" x2="21" y2="12" />
          <line x1="9" y1="18" x2="21" y2="18" />
          <circle cx="4" cy="6" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="4" cy="12" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="4" cy="18" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return null;
  }
}

export default function RichTextEditor({ value, onChange, placeholder, minHeight = "128px", disabled = false }) {
  const editorRef = useRef(null);
  // The HTML this component last emitted, and the value it was last handed.
  // Incoming values that match either are already on screen, so re-writing
  // innerHTML (and losing the caret) is skipped. The raw comparison matters on
  // its own: an emitted value that is nothing but escaped text — "Tom &amp;
  // Jerry" — does not look like HTML coming back in, and would be escaped a
  // second time.
  const lastEmitted = useRef(null);
  const lastValue = useRef(null);
  const [activeCommands, setActiveCommands] = useState({});

  const html = useMemo(() => toEditorHtml(value), [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastValue.current) return;
    if (html === lastEmitted.current) return;
    el.innerHTML = html;
    lastEmitted.current = html;
    lastValue.current = value;
  }, [html, value]);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = isEmptyHtml(el.innerHTML) ? "" : sanitizeHtml(el.innerHTML);
    lastEmitted.current = next;
    lastValue.current = next;
    onChange(next);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    const el = editorRef.current;
    if (!el || document.activeElement !== el) return;
    const state = {};
    COMMANDS.forEach(({ command }) => {
      if (!command) return;
      try {
        state[command] = document.queryCommandState(command);
      } catch {
        state[command] = false;
      }
    });
    setActiveCommands(state);
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshActive);
    return () => document.removeEventListener("selectionchange", refreshActive);
  }, [refreshActive]);

  const run = (command) => {
    const el = editorRef.current;
    if (!el || disabled) return;
    el.focus();
    // Ask for tags (<b>) rather than inline styles, so the stored markup stays
    // inside the whitelist and prints the same way from the PDF template.
    try {
      document.execCommand("styleWithCSS", false, false);
    } catch {
      /* not supported everywhere; the sanitizer covers the fallback */
    }
    document.execCommand(command, false, null);
    emit();
    refreshActive();
  };

  // Paste as plain text: pasted markup from elsewhere is exactly the kind of
  // HTML the sanitizer would throw away anyway.
  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const wordCount = useMemo(() => {
    const text = html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|li|ul|ol)>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&");
    return text.trim().split(/\s+/).filter(Boolean).length;
  }, [html]);

  const showPlaceholder = isEmptyHtml(html);

  return (
    <div className="w-full">
      <div className="self-stretch p-3 border-b border-[#E5E8F3] flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2">
          {COMMANDS.map(({ key, command, label }) =>
            command ? (
              <button
                key={key}
                type="button"
                title={label}
                aria-label={label}
                aria-pressed={!!activeCommands[command]}
                disabled={disabled}
                // mousedown would move focus out of the field and drop the
                // selection before the command runs.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => run(command)}
                className={`p-[2.4px] flex items-center justify-center rounded-md cursor-pointer disabled:opacity-40 ${
                  activeCommands[command] ? "bg-[#EEF0F9] text-[#0065FD]" : "text-[#1A2332] hover:bg-[#EEF0F9]"
                }`}
              >
                <Glyph name={command} />
              </button>
            ) : (
              <span key={key} className="w-px h-4 bg-[#E5E8F3]" />
            ),
          )}
        </div>
        <span className="text-[#7C829A] font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px]">
          {wordCount} words
        </span>
      </div>

      <div className="relative">
        {showPlaceholder && (
          <span className="absolute left-6 top-6 pointer-events-none text-[#868DA6] font-OpenSansRegular text-[16px] leading-6">
            {placeholder}
          </span>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          onInput={emit}
          onBlur={emit}
          onPaste={handlePaste}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          style={{ minHeight }}
          className="rich-text-editor self-stretch w-full p-6 outline-none text-[#1A2332] font-OpenSansRegular text-[16px] leading-6"
        />
      </div>
    </div>
  );
}
